import { useMemo } from "react";

interface Props {
  bookedDates: string[]; // ISO yyyy-mm-dd
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function BookingCalendar({ bookedDates }: Props) {
  const booked = useMemo(() => new Set(bookedDates), [bookedDates]);
  const days = useMemo(() => {
    const arr: Date[] = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, []);

  const today = days[0];
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const rangeLabel = `${days[0].getDate()} ${MONTHS_SHORT[days[0].getMonth()]} – ${days[29].getDate()} ${MONTHS_SHORT[days[29].getMonth()]} ${days[29].getFullYear()}`;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-foreground">Next 30 Days</p>
        <p className="text-xs text-muted-foreground">{rangeLabel}</p>
      </div>
      <div className="grid grid-cols-5 gap-2 sm:grid-cols-6 md:grid-cols-10">
        {days.map((d) => {
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const isBooked = booked.has(iso);
          const isToday = iso === todayStr;
          return (
            <div
              key={iso}
              title={isBooked ? "Booked" : `${WEEKDAYS_LONG[d.getDay()]}, ${d.getDate()} ${MONTHS_LONG[d.getMonth()]}`}
              className={[
                "flex flex-col items-center justify-center rounded-xl border px-2 py-2 text-center transition-colors",
                isBooked
                  ? "border-green-400 bg-green-500 text-white shadow-md ring-2 ring-green-300"
                  : isToday
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border bg-background text-foreground/80",
              ].join(" ")}
            >
              <span className="text-[10px] font-medium uppercase tracking-wide opacity-80">
                {WEEKDAYS[d.getDay()]}
              </span>
              <span className="text-base font-semibold leading-tight">{d.getDate()}</span>
              <span className="text-[10px] opacity-70">
                {MONTHS_SHORT[d.getMonth()]}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full bg-green-500 ring-2 ring-green-300" /> Booked
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full bg-primary/20 ring-1 ring-primary/40" /> Today
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full border border-border bg-background" /> Available
        </span>
      </div>
    </div>
  );
}
